/**
 * Flattens a graph and its layout into the two instance buffers the renderer draws.
 *
 * Two buffers rather than one per visual kind: a node is a billboarded sphere and everything else -
 * a straight reference, a bundled spline, a layer grid, a great circle - is a line segment. Guides
 * and routes share the line buffer because they differ only in color and width, both of which are
 * per-instance, and merging them keeps the whole frame at a fixed number of draw calls whatever the
 * layout and however large the estate.
 *
 * Guides are written first, and `guideCount` is the boundary. The line pass tests depth but does
 * not write it, so order within the buffer is blend order - structure has to go down before the
 * data drawn over it - and the same boundary is what lets the renderer draw the references
 * additively while leaving the structure alone.
 */

import { srgbToLinear } from '../../render/lighting';
import { toModalFill } from './modalFill';
import {
  NO_MARKER,
  type NetworkMarker,
  type NetworkMarkerBody,
} from '../networkMarkers';
import { toMarkerCellOffset } from './networkMarkerAtlas';
import type {
  NetworkGraph,
  NetworkLayout,
  NetworkLineStyle,
  Vec3,
} from '../types';
import {
  toNodeColor,
  toRgb01,
  toTypeColors,
  type EdgeBlend,
  type NetworkTheme,
} from './networkTheme';

/** x, y, z, radius, r, g, b, alpha. */
export const NODE_FLOATS = 8;

/** ax, ay, az, bx, by, bz, r, g, b, alpha, widthPx, distanceAlong, dashOnPx, dashOffPx. */
export const LINE_FLOATS = 16;

/** x, y, z, radius, r, g, b, density. */
export const VOLUME_FLOATS = 8;

/** tip x, y, z, heading x, y, z, cap, aspect, r, g, b, presence, body, hollow. */
export const ARROW_FLOATS = 14;

/** tip x, y, z, heading x, y, z, cap, glyph offset, presence, r, g, b. */
export const PLANAR_MARKER_FLOATS = 12;

/** How a body reaches the shader, which has no enums. Matches `arrowVertex`. */
const MARKER_BODIES: Record<NetworkMarkerBody, number> = {
  cone: 0,
  octahedron: 1,
  sphere: 2,
};

export type NetworkInstances = {
  nodes: Float32Array;
  nodeCount: number;
  lines: Float32Array;
  lineCount: number;
  /**
   * Group regions, as tinted balls of gas.
   *
   * A third buffer rather than a third use of the line one, because a region is not a line. It is
   * billboarded like a node and shaded like nothing else here: the fragment integrates a density
   * through the sphere rather than lighting a surface, so it needs its own pipeline whatever the
   * buffer layout - and sharing a buffer with a primitive that is drawn by a different shader buys
   * nothing but a branch.
   */
  volumes: Float32Array;
  volumeCount: number;
  /**
   * How many of `lineCount` are guides, which the buffer holds first.
   *
   * Carried so the renderer can draw the two halves under different blending. Guides must not
   * accumulate - a grid whose crossings are brighter than its lines is a pattern the eye follows
   * instead of the data - and an additive edge pass must. One buffer, two draws, one boundary.
   */
  guideCount: number;
  /**
   * How many routes carry the travelling pulse, which is 0 unless something is focused.
   *
   * Carried so the caller can decide whether to drive a clock at all. A view with nothing to
   * animate should be drawing on demand, not sixty times a second - and the alternative, asking
   * whether a focus exists, is not the same question: a focus that lights no references is a
   * perfectly ordinary thing to hover.
   */
  pulseCount: number;
  /**
   * One cone per reference that has room for one, at the surface of the mark it arrives at.
   *
   * Geometry rather than an impostor, which is the one place this renderer departs from the
   * decision the rest of it rests on. The sphere and the region are impostors because a tessellated
   * sphere is hundreds of triangles *per node* and an estate has thousands of nodes; a ten-sided
   * cone is twenty triangles and there is one per reference, so the count that argument turns on
   * never arrives. Against that, a cone has no closed-form ray intersection as cheap as a sphere's
   * and its silhouette is an ellipse plus two tangent lines - so the impostor here would be the
   * fragile option rather than the cheap one.
   */
  arrows: Float32Array;
  arrowCount: number;
  /**
   * The flat glyphs, as billboards of the 2D artwork.
   *
   * A second buffer rather than a flag on the first, because the two are drawn by different
   * shaders: one lights a surface the scene occludes, the other samples an atlas and faces the
   * camera. A branch in one pipeline would carry the geometry of both for every marker.
   */
  planarMarkers: Float32Array;
  planarMarkerCount: number;
};

/**
 * What the reader is pointing at, which is either a mark or the space a group occupies.
 *
 * Two kinds rather than one index, because a region is not one of the marks and cannot be named
 * by one. Its node carries no weight - that is how an expanded group says it is a region - so
 * there is nothing at its centre to point at, and its neighborhood is the union of its contents'.
 *
 * `index` is into `graph.nodes` for a mark and into `layout.volumes` for a region.
 */
export type NetworkFocus =
  | { kind: 'node'; index: number }
  | { kind: 'region'; index: number }
  /** `index` is into `layout.routes`, which `layout.links` and `edgeIndices` are parallel to. */
  | { kind: 'edge'; index: number }
  /**
   * Several marks at once, by index into `positions`.
   *
   * What a search answers. The others each stand for one thing a reader pointed at; this one is
   * every thing that answered a question, which is why it is a set rather than an index.
   */
  | { kind: 'marks'; indices: readonly number[] };

export type PackNetworkArgs = {
  graph: NetworkGraph;
  layout: NetworkLayout;
  theme: NetworkTheme;
  /**
   * What is under the cursor, whose neighborhood stays lit while everything else recedes.
   *
   * `null` is the resting state and is not the same as "nothing is near the cursor": the focus
   * survives a pointer that has left the canvas during a drag, so the highlight does not flicker
   * while the graph is being turned.
   */
  focus: NetworkFocus | null;
  /**
   * Relative size per mark, from `toRelativeNodeSizes`, parallel to `graph.nodes`.
   *
   * The same array `buildNetworkLayout` was given. Passed rather than recomputed here, so a
   * renderer cannot size a mark by one rule while the layout reserved space for it by another.
   */
  nodeSizes: readonly number[];
  /**
   * World units per unit of relative size.
   *
   * Ignored where the layout reserved space for its marks - see `NetworkLayout.nodeRadii`. That is
   * the grouped layout and only it: there, a mark's size is a property of the packing rather than a
   * dial, because the packing's non-overlap guarantee is exactly the claim that it is.
   */
  nodeScale: number;
  edgeWidth: number;
  guideWidth: number;
  /** Base opacity for a reference edge before any focus dimming. */
  edgeOpacity: number;
  /**
   * How thick a group's cloud is where it is thickest, as an optical depth.
   *
   * An optical depth rather than an opacity, because that is the quantity the region is drawn
   * from: the fragment integrates this through the sphere and takes `1 - exp(-depth)`, so two
   * nested regions compound the way two clouds do instead of summing past 1 and clipping.
   */
  groupCloudDensity: number;
};

/**
 * An authored color as the linear radiance the shaders work in.
 *
 * At the boundary, once per distinct color, for the reason `toRgb01` gives about the palette: the
 * renderer's arithmetic is linear and the palette is authored and validated encoded, so the
 * conversion belongs here rather than as a second palette written in linear or as a decode in every
 * fragment.
 */
const toLinearRgb = ([red, green, blue]: readonly [
  number,
  number,
  number,
]): readonly [number, number, number] => [
  srgbToLinear(red),
  srgbToLinear(green),
  srgbToLinear(blue),
];

/**
 * Dash and gap in CSS pixels, per style.
 *
 * The canvas views' own numbers, so a reference that is dashed in the block diagram is dashed the
 * same way here: `lineTypeDashArrays` is `[10, 5]` for dashed and `[2]` for dotted, and a
 * single-entry dash array means the gap equals the dash.
 *
 * CSS pixels rather than device pixels, unlike that table, which bakes `devicePixelRatio` in at
 * module load. The shader already has the ratio as a uniform and applies it to the line width, so
 * a dash scales with the display the same way the line it sits on does - and a window dragged to a
 * second monitor does not keep the first one's dash length.
 */
const DASH_PATTERNS: Record<
  NetworkLineStyle,
  readonly [on: number, off: number]
> = {
  solid: [0, 0],
  dashed: [10, 5],
  dotted: [2, 2],
};

/**
 * The dash and gap a style is drawn with, in CSS pixels.
 *
 * Exported so the legend can draw its swatch with the same numbers the renderer uses. A legend
 * whose dashes are its own invention is a legend for a different picture.
 */
export const toDashPattern = (
  lineStyle: NetworkLineStyle
): readonly [on: number, off: number] => DASH_PATTERNS[lineStyle];

/** What an unfocused edge fades to. Not 0: context that vanishes is context lost. */
const DIMMED_EDGE_ALPHA = 0.05;

/**
 * What a reference's opacity is divided by, so the density of the drawn result holds steady.
 *
 * `edgeOpacity` is a property of a reference. How much of the screen a reference covers is a
 * property of the layout: a straight layout draws one segment per reference, and a curved one
 * samples twenty of them along paths it shares with every other reference in the same bundle. So
 * the setting that reads as faint threads on a force layout accumulates to a solid white spine down
 * every bundle - which the package's own Storybook worked around with a table of per-layout
 * opacities the app never had, so the app drew its bundles at three times the weight the package
 * knew they needed.
 *
 * Measured off the routes rather than declared per layout kind, because the quantity that matters is
 * how many times a pixel is covered and the routes are where that is visible - a new layout gets the
 * right weight without being added to a table.
 *
 * The cube root is fitted rather than derived. It earns its place by reproducing all four opacities
 * that had been tuned by eye, from one rule instead of a table: 20 samples is 19 segments, the cube
 * root of that is 2.67, and 0.42 over 2.67 is 0.157 against the 0.16 and 0.14 that were chosen by
 * looking.
 */
const toOverdrawScale = (routeSegments: number, routeCount: number): number =>
  Math.cbrt(Math.max(1, routeSegments / Math.max(1, routeCount)));

/**
 * What the same presence costs in opacity, against each surface.
 *
 * `edgeOpacity` says how present a reference should be; how much alpha that takes depends on what
 * it is blending toward, and the two surfaces are at opposite ends of the range.
 *
 * Both numbers moved once a reference started carrying its own color, because the legend now names
 * that color and a legend is a promise. A quarter was right when every reference was the same grey
 * and density was the only reading in the pass; with a color to carry, a lone reference drawn at a
 * quarter of it reads as a different, duller color than the swatch beside it. Six tenths keeps the
 * bright core where a bundle converges while leaving a single cord recognisably red or blue - the
 * cost is that saturation now arrives at around ten overlapping references rather than twenty.
 *
 * Alpha over a near-white surface loses the *hue* rather than the lightness, which is the worse
 * failure and why these two are so far apart. Adding six tenths of a blue onto near-black still
 * reads as blue. Compositing the same blue at 0.42 over `#fcfcfb` gives `rgb(203, 216, 239)` where
 * a grey reference gives `rgb(215, 218, 222)`: fifteen units apart in one channel is not a subtle
 * color, it is no color. Blending in linear made it worse rather than better, for the same reason
 * the light theme needed less fog - mixing toward a bright background lifts a midtone further than
 * mixing encoded values did. So the alpha path runs a straight layout's references at full opacity,
 * which is the only value at which a drawn line matches the swatch promised for it.
 *
 * Clamped at 1 by the caller, since a boost past opaque means nothing. A curved layout still
 * divides by its overdraw, so a lone reference on the bundled sphere stays lighter than its swatch
 * - twenty samples of it overlap, and the value that makes one thread exact makes the bundle a
 * solid block.
 */
const EDGE_ALPHA_SCALE: Record<EdgeBlend, number> = {
  additive: 0.6,
  alpha: 2.4,
};

/**
 * How much more present a focused reference is than a resting one.
 *
 * A multiple of the resting alpha, so a focused set keeps the density scaling that turns
 * overlapping references into shading. Past about this the bundle stops accumulating and starts
 * clipping, since the resting value is already most of the way up on a light surface.
 */
const FOCUSED_EDGE_BOOST = 2;

/**
 * How wide an arrowhead is at its base, as a fraction of its length.
 *
 * Its *length* is not here: a marker is sized in pixels, like the ribbon it sits on rather than
 * like the marks around it, so the shader resolves it against the viewport and `ARROW_PIXELS`
 * lives there. Sized in world units instead it came out as a cone the size of a component under a
 * close camera and vanished under a distant one.
 *
 * Absolute either way, rather than a multiple of the mark it points at. A mark's radius spans a
 * factor of twenty across one estate - `toRelativeNodeSizes` caps the ratio at eight either side
 * of the typical one - and an arrowhead that tracked it would be a different glyph on every
 * reference, which reads as a second encoding rather than as the same mark repeated.
 */
const ARROW_ASPECT = 0.46;

/**
 * The most of a reference an arrowhead may take up, however close the camera gets.
 *
 * The glyph is sized in pixels, so zooming in grows it against a reference whose own width is
 * fixed in pixels too - but not against the reference's *length*, which is world-space and grows
 * with the zoom. Without a ceiling a short reference under a close camera is swallowed by the
 * marker on the end of it.
 */
const ARROW_ROUTE_SHARE = 0.4;

/**
 * The shortest drawn stretch that still gets an arrowhead, in the units of the fitted cube.
 *
 * Not about the glyph overflowing - `ARROW_ROUTE_SHARE` keeps it inside its reference at any zoom.
 * This is about a reference that is a sliver of the whole picture, where a marker is decoration on
 * something too short to read as a direction in the first place. Below it the reference is drawn
 * plain, which is what it was before.
 */
const ARROW_MINIMUM_ROUTE = 0.02;

/**
 * What the same cloud costs against each surface.
 *
 * The same shape of problem as `EDGE_ALPHA_SCALE`, further apart than the edges needed, and both
 * ends measured off the drawn result rather than argued from the alpha.
 *
 * On black a tenth of a hue added in linear encodes to a little under a third on screen, so the
 * dark surface wants the setting as written. Mixing the same hue *toward* near-white moves almost
 * nowhere by comparison: at 0.17 alpha a saturated orange takes `#fcfcfb` about two encoded percent
 * toward itself, which is a region nobody can see. Five times the density puts the tint at a third,
 * which is where it reads - and is still the ceiling rather than a preference, because past about
 * twice this the references crossing behind a region wash out into it.
 */
const VOLUME_DENSITY_SCALE: Record<EdgeBlend, number> = {
  additive: 1,
  alpha: 5,
};

/**
 * How much thicker the region under the cursor is drawn than it is at rest.
 *
 * Needed because the dimming alone cannot pick a region out. Everything a hovered region contains
 * is lit, so its parent is lit too - it contains the same marks - and on a nested grouping that
 * is every ancestor up to the root, all of them holding their resting density around the one the
 * reader is pointing at. The boost is the only channel that says which of the lit clouds is the
 * answer.

 * Modest, and deliberately below the ceiling `VOLUME_DENSITY_SCALE` describes: past about twice
 * the resting density the references crossing behind a region wash out into it, which on the
 * hovered region is exactly the wrong thing to lose.
 */
const FOCUSED_VOLUME_DENSITY_BOOST = 1.8;

/**
 * Whether two focuses name the same thing.
 *
 * So a pointer crossing one mark keeps the same target object, and everything memoised on it -
 * the repack above all - runs when what is focused changes rather than on every pixel moved.
 */
export const isSameFocus = (
  left: NetworkFocus,
  right: NetworkFocus
): boolean => {
  if (left.kind !== right.kind) return false;
  if (left.kind === 'marks' || right.kind === 'marks') {
    return (
      left.kind === 'marks' &&
      right.kind === 'marks' &&
      left.indices.length === right.indices.length &&
      left.indices.every((index, at) => index === right.indices[at])
    );
  }
  return left.index === right.index;
};

/**
 * The marks the focus is *about*, before any neighbor is added.
 *
 * One for a mark. For a region, everything inside it - which is what makes hovering a group read
 * as hovering the estate it contains rather than as hovering a sphere: the group's own node is
 * weightless and connected to nothing, so seeding from it would light nothing at all.
 */
const toFocusSeeds = (
  layout: NetworkLayout,
  focus: NetworkFocus | null
): ReadonlySet<number> | null => {
  if (focus === null) return null;
  if (focus.kind === 'node') return new Set([focus.index]);
  /* Empty rather than null when a search found nothing, so the picture does not silently revert
     to the unfiltered one: nothing lights, and the whole estate recedes. */
  if (focus.kind === 'marks') return new Set(focus.indices);

  /* A reference stands for the pair it joins, which is the whole of what a reader is asking when
     they point at one in a hairball: not what it is, but what it connects. */
  if (focus.kind === 'edge') {
    const link = layout.links[focus.index];
    return link === undefined ? null : new Set([link.source, link.target]);
  }

  const volume = layout.volumes[focus.index];
  return volume === undefined ? null : new Set(volume.markIndices);
};

/**
 * The seeds and their neighbors, by index, or `null` when nothing should recede.
 *
 * Includes the seeds themselves, so a caller can ask one question - "is this lit" - rather than
 * special-casing the center of its own neighborhood.
 *
 * `null` for a lone mark with no neighbors, which is not the same as dimming everything and
 * lighting one mark. Dimming buys separation of a neighborhood from its context, and an isolated
 * node has no neighborhood to separate - so the trade is the whole estate for nothing. It is
 * reachable on the first hover of a real viewpoint, where the people and the capabilities carry
 * no references of their own: a viewpoint loaded as 670 components went to a field of ghosts
 * behind one tooltip.
 *
 * A region is never that case, even one holding a single unconnected mark. What a reader asked
 * for there is the group separated from the estate rather than a neighborhood separated from its
 * context, and the group is on screen whether or not anything in it carries a reference.
 */
const toFocusSet = (
  layout: NetworkLayout,
  focus: NetworkFocus | null,
  seeds: ReadonlySet<number> | null
): ReadonlySet<number> | null => {
  if (seeds === null) return null;

  /* Not expanded for a reference. Its seeds are already the answer - the two ends - and adding
     their neighborhoods would light everything either end touches, which on a hub is most of the
     estate and is a different question from the one being asked. */
  if (focus?.kind === 'edge') return seeds;

  /* Nor for a search. The answer is what matched; a neighborhood around each result would bury
     two matches on a hub under everything they touch. */
  if (focus?.kind === 'marks') return seeds;

  const lit = new Set<number>(seeds);
  for (const { source, target } of layout.links) {
    if (seeds.has(source)) lit.add(target);
    if (seeds.has(target)) lit.add(source);
  }

  return focus?.kind === 'region' || lit.size > 1 ? lit : null;
};

/** What a guide carries: no length to be a fraction of, and no pulse. */
const NO_FLOW: readonly [number, number] = [0, 0];

const toRouteLength = (route: readonly Vec3[]): number => {
  let total = 0;
  for (let at = 0; at + 1 < route.length; at++) {
    total += Math.hypot(
      route[at + 1][0] - route[at][0],
      route[at + 1][1] - route[at][1],
      route[at + 1][2] - route[at][2]
    );
  }
  return total;
};

/**
 * Where a segment crosses out of a sphere, as a fraction along it.
 *
 * The quadratic rather than a walk in steps: a route's samples are far apart next to a mark the
 * size of a node, so stepping would put the crossing wherever the sampling happened to land and an
 * arrowhead would sit visibly off the surface it is meant to touch.
 */
const toSphereExit = (
  inside: Vec3,
  outside: Vec3,
  center: Vec3,
  radius: number
): number => {
  const dx = outside[0] - inside[0];
  const dy = outside[1] - inside[1];
  const dz = outside[2] - inside[2];
  const fx = inside[0] - center[0];
  const fy = inside[1] - center[1];
  const fz = inside[2] - center[2];

  const a = dx * dx + dy * dy + dz * dz;
  if (a <= 1e-12) return 1;
  const b = 2 * (fx * dx + fy * dy + fz * dz);
  const c = fx * fx + fy * fy + fz * fz - radius * radius;
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return 1;

  return Math.min(1, Math.max(0, (-b + Math.sqrt(discriminant)) / (2 * a)));
};

const lerp = (from: Vec3, to: Vec3, at: number): Vec3 => [
  from[0] + (to[0] - from[0]) * at,
  from[1] + (to[1] - from[1]) * at,
  from[2] + (to[2] - from[2]) * at,
];

const toDistance = (from: Vec3, to: Vec3): number =>
  Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]);

/**
 * The stretch of a route that is outside the marks at either end of it.
 *
 * A route runs centre to centre, so its last stretch is inside the mark it arrives at - which is
 * invisible, because the node pass writes depth before the line pass reads it. That was free while
 * a reference had nothing to say at its end. An arrowhead there would be buried in the sphere it
 * points at, so the route has to stop at the surface instead.
 *
 * `null` when the marks overlap or touch, which happens wherever the packing seats a child on its
 * parent: there is no stretch between them to draw, and nothing for an arrowhead to sit on.
 */
export const toTrimmedRoute = (
  route: readonly Vec3[],
  fromRadius: number,
  toRadius: number
): Vec3[] | null => {
  if (route.length < 2) return null;

  /**
   * Measured against the route's length rather than against the indices the walks below stop on.
   *
   * A straight route is two points, both of them *at* the centres they are being measured from, so
   * both walks run off their own end and the indices cross - which says nothing about whether the
   * marks leave room, and rejected every reference in a force layout. What decides it is whether
   * the two radii together are shorter than the route.
   */
  if (fromRadius + toRadius >= toRouteLength(route)) return null;

  const source = route[0];
  const target = route[route.length - 1];

  let first = 0;
  while (
    first + 1 < route.length &&
    toDistance(route[first], source) < fromRadius
  ) {
    first += 1;
  }
  let last = route.length - 1;
  while (last > 0 && toDistance(route[last], target) < toRadius) {
    last -= 1;
  }

  const start =
    first === 0
      ? route[0]
      : lerp(
          route[first - 1],
          route[first],
          toSphereExit(route[first - 1], route[first], source, fromRadius)
        );
  const end =
    last === route.length - 1
      ? route[last]
      : lerp(
          route[last + 1],
          route[last],
          toSphereExit(route[last + 1], route[last], target, toRadius)
        );

  /* The endpoints replace the samples they were interpolated from rather than joining them, which
     a slice from `first` would do - and a repeated point is a zero-length segment whose direction
     is undefined, which is what an arrowhead takes its heading from. */
  return [
    start,
    ...route.slice(
      first === 0 ? 1 : first,
      last === route.length - 1 ? last : last + 1
    ),
    end,
  ];
};

const writeLine = (
  target: Float32Array,
  at: number,
  from: readonly [number, number, number],
  to: readonly [number, number, number],
  [r, g, b]: readonly [number, number, number],
  alpha: number,
  width: number,
  distanceAlong: number,
  dashOn: number,
  dashOff: number,
  /** The whole polyline's length in layout units, and whether it carries the travelling pulse. */
  flow: readonly [number, number]
): void => {
  target[at] = from[0];
  target[at + 1] = from[1];
  target[at + 2] = from[2];
  target[at + 3] = to[0];
  target[at + 4] = to[1];
  target[at + 5] = to[2];
  target[at + 6] = r;
  target[at + 7] = g;
  target[at + 8] = b;
  target[at + 9] = alpha;
  target[at + 10] = width;
  /* Distance from the start of this line's own polyline, in layout units. The dash phase is
     continuous along a route because of this: measured per segment instead, every one of a bundled
     spline's twenty samples would restart the pattern and a dashed reference would come out as
     twenty identical short dashes regardless of its length. */
  target[at + 11] = distanceAlong;
  target[at + 12] = dashOn;
  target[at + 13] = dashOff;
  /* The length `distanceAlong` is a fraction of, which is how the shader turns a distance into a
     position along the reference - 0 at the source and 1 at the target. Zero for a guide, which
     has no source and no target. */
  target[at + 14] = flow[0];
  target[at + 15] = flow[1];
};

export const packNetworkInstances = ({
  graph,
  layout,
  theme,
  focus,
  nodeSizes,
  nodeScale,
  edgeWidth,
  guideWidth,
  edgeOpacity,
  groupCloudDensity,
}: PackNetworkArgs): NetworkInstances => {
  const seeds = toFocusSeeds(layout, focus);
  const lit = toFocusSet(layout, focus, seeds);
  const typeColors = toTypeColors(graph.nodes);
  const guideInk = toLinearRgb(theme.guideInk);
  const edgeInk = toLinearRgb(theme.edgeInk);
  /* Parsed once per distinct color rather than once per node: a large estate is thousands of
     nodes over a handful of colors, and `toRgb01` is a string parse. */
  const channelsOf = new Map<string, readonly [number, number, number]>();

  const nodeCount = Math.min(graph.nodes.length, layout.positions.length);
  const nodes = new Float32Array(nodeCount * NODE_FLOATS);
  /* Kept only when something is going to ask what color a mark came out, which is the grouped
     layout and nothing else. Every other layout would be paying for an array per repack - and a
     repack happens on every change of hover. */
  const nodeColors: string[] | null =
    layout.volumes.length > 0 ? new Array<string>(nodeCount) : null;

  for (let index = 0; index < nodeCount; index++) {
    const node = graph.nodes[index];
    const [x, y, z] = layout.positions[index];
    const hex = toNodeColor(node, typeColors);
    if (nodeColors) nodeColors[index] = hex;
    const cached = channelsOf.get(hex);
    const color = cached ?? toLinearRgb(toRgb01(hex));
    if (!cached) channelsOf.set(hex, color);
    const at = index * NODE_FLOATS;

    nodes[at] = x;
    nodes[at + 1] = y;
    nodes[at + 2] = z;
    /* What the layout set aside for this mark where it set anything aside, and the relative size
       against the absolute dial where it did not. The square root that makes area rather than
       radius linear in the quantity has already been taken, by `toRelativeNodeSizes`. */
    nodes[at + 3] =
      layout.nodeRadii?.[index] ?? (nodeSizes[index] ?? 0) * nodeScale;
    nodes[at + 4] = color[0];
    nodes[at + 5] = color[1];
    nodes[at + 6] = color[2];
    nodes[at + 7] = !lit || lit.has(index) ? 1 : theme.dimmedNodeEmphasis;
  }

  const guideSegments = layout.guides.reduce(
    (total, guide) => total + Math.max(0, guide.length - 1),
    0
  );
  const toNodeRadius = (index: number): number =>
    layout.nodeRadii?.[index] ?? (nodeSizes[index] ?? 0) * nodeScale;

  /**
   * Every route cut back to the stretch between the two marks it joins.
   *
   * Resolved once, before anything counts segments or writes one: trimming changes how many
   * segments there are, and the segment count is what the overdraw scale divides opacity by. Two
   * passes over the same routes with different answers would light the picture by one number and
   * draw it with another.
   *
   * A route whose marks leave no room keeps its full length and gets no arrowhead.
   */
  const routes = layout.routes.map((route, edgeIndex) => {
    const link = layout.links[edgeIndex];
    const trimmed =
      link === undefined
        ? null
        : toTrimmedRoute(
            route,
            toNodeRadius(link.source),
            toNodeRadius(link.target)
          );

    return { points: trimmed ?? route, isTrimmed: trimmed !== null };
  });

  const routeSegments = routes.reduce(
    (total, { points }) => total + Math.max(0, points.length - 1),
    0
  );
  const lines = new Float32Array((guideSegments + routeSegments) * LINE_FLOATS);
  let cursor = 0;

  /* Two ends apiece, since a reference type may decorate both - a UML composition is a filled
     diamond at the source and an open arrow at the target. */
  const arrows = new Float32Array(routes.length * 2 * ARROW_FLOATS);
  let arrowCursor = 0;
  const planarMarkers = new Float32Array(
    routes.length * 2 * PLANAR_MARKER_FLOATS
  );
  let planarCursor = 0;

  /**
   * Writes whichever of the two buffers a marker belongs in, or neither.
   *
   * The split is by how a glyph can exist in three dimensions rather than by what it means: a
   * solid body is geometry the scene lights and occludes, and flat notation is a billboard of the
   * 2D artwork. See `networkMarkers.ts` for why the second kind cannot be anything else.
   */
  const writeMarker = ({
    marker,
    tip,
    behind,
    cap,
    presence,
  }: {
    marker: NetworkMarker;
    tip: Vec3;
    behind: Vec3;
    cap: number;
    presence: number;
  }): void => {
    if (marker.kind === 'none') return;
    const span = toDistance(behind, tip);
    if (span <= 1e-9) return;

    const heading: Vec3 = [
      (tip[0] - behind[0]) / span,
      (tip[1] - behind[1]) / span,
      (tip[2] - behind[2]) / span,
    ];

    if (marker.kind === 'planar') {
      const at = planarCursor;
      planarMarkers.set(tip, at);
      planarMarkers.set(heading, at + 3);
      planarMarkers[at + 6] = cap;
      planarMarkers[at + 7] = toMarkerCellOffset(marker.glyph);
      planarMarkers[at + 8] = presence;
      planarMarkers.set(edgeInk, at + 9);
      planarCursor += PLANAR_MARKER_FLOATS;
      return;
    }

    const at = arrowCursor;
    arrows.set(tip, at);
    arrows.set(heading, at + 3);
    arrows[at + 6] = cap;
    arrows[at + 7] = ARROW_ASPECT;
    /**
     * The theme's edge ink, and never the reference's own color.
     *
     * A marker is notation rather than data. The legend already names what each reference type
     * looks like, in its color and its dash, and a marker repeating that says nothing the line
     * beside it has not said - while a cone in a saturated hue is the heaviest thing on screen at
     * every junction. Monochrome is also simply what these glyphs are drawn as in 2D.
     */
    arrows.set(edgeInk, at + 8);
    /**
     * Full presence, or the dim the marks use - and not the reference's own opacity.
     *
     * Spent against the surface rather than as alpha, so the pass stays opaque: it needs no sort,
     * and the depth it writes is honest for the lines after it. The opacity itself is the wrong
     * number to spend, though. `toEdgeAlpha` divides by how much the layout overdraws, because a
     * bundled route lays twenty samples of itself over one another - and a marker is drawn once.
     * Taking that deflated value washed the glyph to within a third of the background on exactly
     * the layout that needs it most.
     */
    arrows[at + 11] = presence;
    arrows[at + 12] = MARKER_BODIES[marker.body];
    arrows[at + 13] = marker.isHollow ? 1 : 0;
    arrowCursor += ARROW_FLOATS;
  };

  const toEdgeAlpha = (
    segments: number,
    routeCount: number,
    boost: number
  ): number =>
    Math.min(
      1,
      (edgeOpacity / toOverdrawScale(segments, routeCount)) *
        EDGE_ALPHA_SCALE[theme.edgeBlend] *
        boost
    );

  const restEdgeAlpha = toEdgeAlpha(routeSegments, layout.routes.length, 1);

  /**
   * Whether a route belongs to the focused neighborhood.
   *
   * One predicate, because two readers have to agree on the answer: the pass that measures the
   * focused set's overdraw and the pass that draws it. Measured over one set and drawn over
   * another, the alpha would be scaled for a density that is not on screen.
   */
  const isFocusedRoute = (edgeIndex: number): boolean => {
    if (lit === null || seeds === null) return false;
    /* The one that was pointed at, and not every reference sharing an end with it. A mark's
       neighborhood is its references; a reference's neighborhood is itself. */
    if (focus?.kind === 'edge') return edgeIndex === focus.index;
    const link = layout.links[edgeIndex];
    return (
      link !== undefined && (seeds.has(link.source) || seeds.has(link.target))
    );
  };

  let focusedRoutes = 0;
  let pulseCount = 0;
  let focusedSegments = 0;
  routes.forEach(({ points }, edgeIndex) => {
    if (!isFocusedRoute(edgeIndex)) return;
    focusedRoutes += 1;
    focusedSegments += Math.max(0, points.length - 1);
  });

  const focusedEdgeAlpha = toEdgeAlpha(
    focusedSegments,
    focusedRoutes,
    FOCUSED_EDGE_BOOST
  );

  for (const guide of layout.guides) {
    for (let at = 0; at + 1 < guide.length; at++) {
      writeLine(
        lines,
        cursor,
        guide[at],
        guide[at + 1],
        guideInk,
        /* Held at full opacity and made recessive by its color instead. A translucent grid
           accumulates where lines cross, so the intersections read as brighter points - which on a
           regular grid is a pattern the eye follows instead of the data. */
        1,
        guideWidth,
        /* Solid, always. A grid or a cage is structure the reader is meant to look past, and a
           dashed one reads as data that means something. */
        0,
        0,
        0,
        NO_FLOW
      );
      cursor += LINE_FLOATS;
    }
  }

  routes.forEach(({ points: route, isTrimmed }, edgeIndex) => {
    /* Through `edgeIndices` rather than by indexing `graph.edges` with the route's own index: the
       two are not parallel, so a dropped self-loop earlier in the list would shift every
       reference after it onto the wrong color. */
    const edge = graph.edges[layout.edgeIndices[edgeIndex]];
    const stroke = edge?.stroke ?? null;
    const cachedStroke = stroke === null ? undefined : channelsOf.get(stroke);
    const resolvedStroke =
      stroke === null
        ? edgeInk
        : (cachedStroke ?? toLinearRgb(toRgb01(stroke)));
    if (stroke !== null && !cachedStroke)
      channelsOf.set(stroke, resolvedStroke);

    const [dashOn, dashOff] = DASH_PATTERNS[edge?.lineStyle ?? 'solid'];
    /* `lit` as well as the route's own answer, so the edges and the nodes agree on whether
       anything is focused at all - `toFocusSet` can decide to dim nothing. An edge with either
       end in the focused set lights, so a group's internal references light with its external
       ones. */
    const alpha =
      lit === null
        ? restEdgeAlpha
        : isFocusedRoute(edgeIndex)
          ? focusedEdgeAlpha
          : DIMMED_EDGE_ALPHA;

    /**
     * The pulse is carried by the references of whatever the reader is pointing at, and by
     * nothing else.
     *
     * Motion tied to attention rather than ambient. A view where every reference is always moving
     * is a view nobody can read a label on, and it would draw a frame sixty times a second over an
     * estate that is not being looked at. Tied to the focus, an idle graph is perfectly still, and
     * what moves is the answer to the question the reader just asked.
     */
    const isPulsing = lit !== null && isFocusedRoute(edgeIndex);
    if (isPulsing) pulseCount += 1;
    const routeLength = toRouteLength(route);
    const flow: readonly [number, number] = [routeLength, isPulsing ? 1 : 0];

    /**
     * One marker per end that declares one, headed along the segment it sits on.
     *
     * Along the last drawn segment rather than from one end of the reference to the other: a
     * bundled route arrives on a curve, and a heading taken from the chord would point a marker
     * off the line it is sitting on. The source end is headed the other way for the same reason
     * the sprite mirrors a start marker - a marker points out of the end it belongs to.
     */
    if (isTrimmed && routeLength >= ARROW_MINIMUM_ROUTE) {
      const presence =
        lit === null || isFocusedRoute(edgeIndex)
          ? 1
          : theme.dimmedNodeEmphasis;
      const cap = routeLength * ARROW_ROUTE_SHARE;

      writeMarker({
        marker: edge?.markerAtTarget ?? NO_MARKER,
        tip: route[route.length - 1],
        behind: route[route.length - 2],
        cap,
        presence,
      });
      writeMarker({
        marker: edge?.markerAtSource ?? NO_MARKER,
        tip: route[0],
        behind: route[1],
        cap,
        presence,
      });
    }

    let distanceAlong = 0;
    for (let at = 0; at + 1 < route.length; at++) {
      writeLine(
        lines,
        cursor,
        route[at],
        route[at + 1],
        resolvedStroke,
        alpha,
        edgeWidth,
        distanceAlong,
        dashOn,
        dashOff,
        flow
      );
      cursor += LINE_FLOATS;
      distanceAlong += Math.hypot(
        route[at + 1][0] - route[at][0],
        route[at + 1][1] - route[at][1],
        route[at + 1][2] - route[at][2]
      );
    }
  });

  const volumes = new Float32Array(layout.volumes.length * VOLUME_FLOATS);

  layout.volumes.forEach(({ center, radius, markIndices }, index) => {
    const isFocusedRegion = focus?.kind === 'region' && focus.index === index;
    const hex =
      nodeColors === null
        ? null
        : toModalFill(markIndices.map(at => nodeColors[at]));
    /* The structure ink where the marks inside carry no color of their own: nothing under the
       region has a component type, so there is no hue it could honestly wear. Reached by a
       grouping whose marks are all untyped rather than by anything this host sends today. */
    const cached = hex === null ? undefined : channelsOf.get(hex);
    const color =
      hex === null ? guideInk : (cached ?? toLinearRgb(toRgb01(hex)));
    if (hex !== null && !cached) channelsOf.set(hex, color);

    /* Lit when any of the focused neighborhood is inside it, so hovering a component leaves the
       region it belongs to standing while the rest of the estate recedes - which is most of what
       a grouping is being read for. Dimmed by the node emphasis rather than by a number of its
       own: a cloud and the marks in it have to recede together or the cloud becomes the picture. */
    const isLit =
      lit === null || markIndices.some(markIndex => lit.has(markIndex));

    const at = index * VOLUME_FLOATS;
    volumes[at] = center[0];
    volumes[at + 1] = center[1];
    volumes[at + 2] = center[2];
    volumes[at + 3] = radius;
    volumes[at + 4] = color[0];
    volumes[at + 5] = color[1];
    volumes[at + 6] = color[2];
    volumes[at + 7] =
      groupCloudDensity *
      VOLUME_DENSITY_SCALE[theme.edgeBlend] *
      (isLit ? 1 : theme.dimmedNodeEmphasis) *
      (isFocusedRegion ? FOCUSED_VOLUME_DENSITY_BOOST : 1);
  });

  return {
    nodes,
    nodeCount,
    lines,
    lineCount: cursor / LINE_FLOATS,
    guideCount: guideSegments,
    pulseCount,
    arrows,
    arrowCount: arrowCursor / ARROW_FLOATS,
    planarMarkers,
    planarMarkerCount: planarCursor / PLANAR_MARKER_FLOATS,
    volumes,
    volumeCount: layout.volumes.length,
  };
};
